import React from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { Icon, HighlightKeyword } from 'bench-internal-theme';
import { colors } from 'bench-style-constants';
import { DataEntry } from '../types';
import FloatingExpandableText from '../../FloatingExpandableText';
import QualityIssuesSelectColumn, {
  QualityIssuesSelectColumnProps,
} from './QualityIssuesSelectColumn';
import DataEntryTableNotesColumn, { NotesColumnProps } from './DataEntryTableNotesColumn';
import {
  AddOnColumn,
  AmountColumn,
  DateColumn,
  IconColumn,
  TextCell,
  Columns1099,
} from './DataEntryTableColumnDefinitions.styles';
import { formatDollarAmount } from '../utils/currency';

const getFormattedDateString = dateString => {
  if (!dateString) {
    return '';
  }
  return new Date(dateString).toLocaleDateString('en-US', { timeZone: 'UTC' });
};

const ICON_COL_SIZE = 30;
const ICON_COL_MIN_SIZE = 10;

const findContraLedgerName = (dataEntry: DataEntry) => {
  if (dataEntry.contraLedgerAccounts.length > 2) return 'Split';
  if (dataEntry.contraLedgerAccounts.length === 1) {
    return dataEntry.contraLedgerAccounts[0].name;
  }
  return null;
};

const isSelectedOrHasQualityIssuesCol: ColumnDef<DataEntry> = {
  accessorFn: (row): QualityIssuesSelectColumnProps => {
    return {
      hasPotentialQualityIssues: !!row.hasPotentialQualityIssues,
      id: row.id,
      dataEntry: row,
      ledgerAccountId: row.ledgerAccount.id,
    };
  },
  id: 'isSelectedOrHasQualityIssues',
  header: 'isSelectedOrHasQualityIssues',
  size: 40,
  minSize: 20,
  enableSorting: false,
  cell: info => {
    const { hasPotentialQualityIssues, id, dataEntry, ledgerAccountId } =
      info.getValue() as QualityIssuesSelectColumnProps;
    return (
      <QualityIssuesSelectColumn
        hasPotentialQualityIssues={hasPotentialQualityIssues}
        id={id}
        dataEntry={dataEntry}
        ledgerAccountId={ledgerAccountId}
      />
    );
  },
};

const transactionDateCol: ColumnDef<DataEntry> = {
  accessorFn: row => row.journalEntry.dateOfTransaction,
  id: 'dateOfTransaction',
  header: 'Date',
  size: 100,
  minSize: 50,
  enableSorting: true,
  cell: info => <DateColumn>{getFormattedDateString(info.getValue())}</DateColumn>,
};

export function removeLeadingAndTrailingQuotes(str: string | undefined) {
  return str
    ?.split(' ')
    .map(word => word.replace(/^['"](.*)['"]$/, '$1'))
    .join(' ');
}

const transactionNameCol = (
  search: string | undefined,
  isQualityIssues: boolean,
): ColumnDef<DataEntry> => {
  return {
    accessorFn: row =>
      isQualityIssues
        ? `${row.journalEntry.vendorName} — ${row.ledgerAccount.name}`
        : `${row.journalEntry.vendorName}`,
    id: 'transactionName',
    header: 'Transaction Name',
    size: 400,
    minSize: 200,
    enableSorting: true,
    cell: info => (
      <TextCell>
        <HighlightKeyword keyword={removeLeadingAndTrailingQuotes(search)}>
          {info.getValue()}
        </HighlightKeyword>
      </TextCell>
    ),
  };
};

const contraLedgerCol = (isAccount: boolean): ColumnDef<DataEntry> => {
  return {
    accessorFn: row => (isAccount ? findContraLedgerName(row) : null),
    id: 'contraLedger',
    header: 'Contra Ledger',
    size: 200,
    minSize: 100,
    enableSorting: false,
    cell: info => <TextCell>{info.getValue()}</TextCell>,
  };
};

const debitCol: ColumnDef<DataEntry> = {
  accessorFn: row => (row.amount >= 0 ? formatDollarAmount(Math.abs(row.amount)) : ''),
  id: 'debit',
  header: 'Debit',
  size: 80,
  minSize: 60,
  enableSorting: false,
  cell: info => <AmountColumn isDebit={true}>{info.getValue()}</AmountColumn>,
};

const creditCol: ColumnDef<DataEntry> = {
  accessorFn: row => (row.amount < 0 ? formatDollarAmount(Math.abs(row.amount)) : ''),
  id: 'credit',
  header: 'Credit',
  size: 80,
  minSize: 60,
  enableSorting: false,
  cell: info => <AmountColumn isDebit={false}>{info.getValue()}</AmountColumn>,
};

const notesCol = (search: string | undefined): ColumnDef<DataEntry> => {
  return {
    accessorFn: (row): Omit<NotesColumnProps, 'search'> => {
      return {
        internalNotes: row.journalEntry?.internalNotes || [],
        comments: row.journalEntry.comments || [],
        clientSuggestion: row.journalEntry.categorization?.clientSuggestion,
      };
    },
    id: 'note',
    header: 'Notes',
    size: 400,
    minSize: 100,
    enableSorting: false,
    cell: info => {
      const { internalNotes, comments, clientSuggestion } = info.getValue() as NotesColumnProps;
      return (
        <DataEntryTableNotesColumn
          internalNotes={internalNotes}
          comments={comments}
          clientSuggestion={clientSuggestion}
          search={search}
        />
      );
    },
  };
};

const hasCommentsCol: ColumnDef<DataEntry> = {
  accessorFn: row => {
    const { comments } = row.journalEntry;
    return comments.length > 0; // || internalNotes.length > 0 || !!clientCatResponse;
  },
  id: 'hasComments',
  header: 'hasComments',
  size: ICON_COL_SIZE,
  minSize: ICON_COL_MIN_SIZE,
  enableSorting: false,
  cell: info => (
    <IconColumn title="Has client comments, client categorization response, or internal notes">
      {!!info.getValue() && <Icon color={colors.dust} icon="speechBubble" size="small" />}
    </IconColumn>
  ),
};

const isLedgerSelectedByClientCol = {
  accessorFn: row => {
    return (
      row.ledgerAccount &&
      !!row.journalEntry.categorization &&
      !!row.journalEntry.categorization.selectedLedgerAccount &&
      row.journalEntry.categorization.selectedLedgerAccount.id === row.ledgerAccount.id
    );
  },
  id: 'isLedgerSelectedByClient',
  header: 'isLedgerSelectedByClient',
  size: ICON_COL_SIZE,
  minSize: ICON_COL_MIN_SIZE,
  enableSorting: false,
  cell: info => (
    <IconColumn title="Client selected this ledger account">
      {!!info.getValue() && <Icon color={colors.dust} icon="circleCheck" size="small" />}
    </IconColumn>
  ),
};

const hasAttachmentCol = {
  accessorFn: row => !!row.journalEntry?.file,
  id: 'hasAttachment',
  header: 'hasAttachment',
  size: ICON_COL_SIZE,
  minSize: ICON_COL_MIN_SIZE,
  enableSorting: false,
  cell: info => (
    <IconColumn title={info.row.original.journalEntry?.file?.displayName}>
      {!!info.getValue() && <Icon color={colors.dust} icon="paperclip" size="small" />}
    </IconColumn>
  ),
};

const hasFlaggedInternalNoteCol = {
  accessorFn: row => row.journalEntry.internalNotes?.some(({ flagged }) => flagged),
  id: 'hasFlaggedInternalNote',
  header: 'hasFlaggedInternalNote',
  size: ICON_COL_SIZE,
  minSize: ICON_COL_MIN_SIZE,
  enableSorting: false,
  cell: info => (
    <IconColumn title="Has flagged internal note(s)">
      {!!info.getValue() && <Icon color={colors.dust} icon="flagFilled" size="small" />}
    </IconColumn>
  ),
};

const addOnCol = {
  accessorFn: row => row.journalEntry.addOn?.name,
  id: 'addOn',
  header: 'Add-On',
  enableSorting: false,
  cell: info => (
    <AddOnColumn>
      <FloatingExpandableText>{info.getValue()}</FloatingExpandableText>
    </AddOnColumn>
  ),
};

const contractorCol = {
  accessorFn: row =>
    row.journalEntry.contractor?.id
      ? row.journalEntry.contractor?.contractorName || 'Unknown'
      : null,
  id: 'contractor',
  header: 'Contractor',
  size: 200,
  enableSorting: false,
  cell: info => (
    <Columns1099>
      <TextCell>{info.getValue()}</TextCell>
    </Columns1099>
  ),
};

const source1099Col = {
  accessorFn: row =>
    row.journalEntry.contractor?.id ? row.journalEntry.contractor?.source1099 || 'Unknown' : null,
  id: 'source1099',
  header: 'Sources',
  size: 200,
  enableSorting: false,
  cell: info => (
    <Columns1099>
      <TextCell>{info.getValue()}</TextCell>
    </Columns1099>
  ),
};

export const getColumnDefinitions = (
  search: string,
  isAccount: boolean,
  isQualityIssues: boolean,
): ColumnDef<DataEntry>[] => {
  return [
    isSelectedOrHasQualityIssuesCol,
    transactionDateCol,
    transactionNameCol(search, isQualityIssues),
    contraLedgerCol(isAccount),
    debitCol,
    creditCol,
    contractorCol,
    source1099Col,
    notesCol(search),
    hasCommentsCol,
    isLedgerSelectedByClientCol,
    hasAttachmentCol,
    hasFlaggedInternalNoteCol,
    addOnCol,
  ];
};
